import { useCallback } from 'react';
import { create } from 'zustand';
import { getToken } from '../api/client';
import { partner } from '../api/partner';
import { I18N_REFRESH_MS, LOCALE_KEY } from '../config';
import { safeStorage } from '../lib/storage';
import { uk, type DictKey } from './uk';

/**
 * Runtime translations (namespace `partner`). The built-in Ukrainian dictionary is the source of keys;
 * every other language — and edits to Ukrainian — come from `GET /i18n/partner` (DB, editable in the CRM).
 * A new language added in the CRM shows up here with zero code changes.
 *
 * Resolution of a key: chosen locale (DB) → uk (DB) → built-in uk → the key itself.
 */

export type Vars = Record<string, string | number>;
export type Strings = Record<string, Record<string, string>>;
export interface LocaleInfo {
  code: string;
  name: string;
  position?: number;
}

export const FALLBACK = 'uk';
/** Shown until the API answers (or when it is unreachable): the four seeded languages. */
export const BUILTIN_LOCALES: LocaleInfo[] = [
  { code: 'uk', name: 'Українська', position: 0 },
  { code: 'ro', name: 'Română', position: 1 },
  { code: 'ru', name: 'Русский', position: 2 },
  { code: 'en', name: 'English', position: 3 },
];

export function format(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** Pure resolution (tested): chosen locale → DB uk → built-in uk → key. Empty strings count as missing. */
export function resolveString(strings: Strings, locale: string, key: string): string {
  const pick = (v: string | undefined) => (v && v.trim() ? v : undefined);
  return pick(strings[locale]?.[key]) ?? pick(strings[FALLBACK]?.[key]) ?? pick((uk as Record<string, string>)[key]) ?? key;
}

export function translate(strings: Strings, locale: string, key: DictKey | string, vars?: Vars): string {
  return format(resolveString(strings, locale, key), vars);
}

/** "uk-UA" → "uk", "mo"/"ro-MD" → "ro", "ua" → "uk". Returns the lowercase base code or null. */
export function normalizeLocale(v: string | null | undefined): string | null {
  if (!v) return null;
  const b = v.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  if (!b) return null;
  if (b === 'mo') return 'ro';
  if (b === 'ua') return 'uk';
  return /^[a-z]{2,3}$/.test(b) ? b : null;
}

/** First candidate that is an available language; otherwise uk. Priority: chosen → account → device. */
export function pickLocale(available: string[], chosen: string | null, account: string | null, device: string | null): string {
  for (const c of [chosen, account, device]) {
    const n = normalizeLocale(c);
    if (n && available.includes(n)) return n;
  }
  return FALLBACK;
}

/** Built-in dictionary as a flat `{key: text}` object (sorted keys) — what scripts/export-i18n.mjs writes. */
export function exportBuiltin(): Record<string, string> {
  return Object.fromEntries(Object.entries(uk).sort(([a], [b]) => a.localeCompare(b)));
}

interface I18nState {
  locale: string;
  locales: LocaleInfo[];
  strings: Strings;
  /** Explicit choice on this device (localStorage xq.partner.locale). */
  chosen: string | null;
  /** me.user.locale. */
  account: string | null;
  loaded: boolean;
  setLocale: (code: string) => void;
  setAccountLocale: (code: string | null | undefined) => void;
  /** Fetch GET /i18n/partner. Failures keep what we have (built-in uk at worst). */
  load: () => Promise<void>;
}

function deviceLocale(): string | null {
  try {
    return globalThis.navigator?.language ?? null;
  } catch {
    return null;
  }
}

const codes = (ls: LocaleInfo[]) => ls.map((l) => l.code);

export const useI18n = create<I18nState>((set, get) => {
  const chosen = normalizeLocale(safeStorage.get(LOCALE_KEY));
  const recompute = (p: Partial<Pick<I18nState, 'chosen' | 'account' | 'locales'>>) => {
    const s = { ...get(), ...p };
    set({ ...p, locale: pickLocale(codes(s.locales), s.chosen, s.account, deviceLocale()) });
  };
  return {
    chosen,
    account: null,
    locales: BUILTIN_LOCALES,
    strings: {},
    loaded: false,
    locale: pickLocale(codes(BUILTIN_LOCALES), chosen, null, deviceLocale()),
    setLocale(code) {
      const n = normalizeLocale(code);
      if (!n) return;
      safeStorage.set(LOCALE_KEY, n);
      recompute({ chosen: n });
      // signed in → remember on the account too (another phone opens in the same language); best effort
      if (getToken()) void partner.saveLocale(n).catch(() => undefined);
    },
    setAccountLocale(code) {
      recompute({ account: normalizeLocale(code) });
    },
    async load() {
      try {
        const r = await partner.i18n('partner');
        const strings = r.strings ?? {};
        // a language without a single translated string would show only Ukrainian — offer it once it has texts
        const hasTexts = (code: string) => code === FALLBACK || Object.keys(strings[code] ?? {}).length > 0;
        const fromApi = (r.locales ?? []).filter((l) => hasTexts(l.code));
        const locales = (fromApi.length ? [...fromApi] : BUILTIN_LOCALES).sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.code.localeCompare(b.code),
        );
        if (!locales.some((l) => l.code === FALLBACK)) locales.unshift(BUILTIN_LOCALES[0]!);
        set({ strings, loaded: true });
        recompute({ locales });
      } catch {
        set({ loaded: true });
      }
    },
  };
});

// The language chosen in another tab of the app applies here too right away — otherwise this tab kept showing
// one language and switched to the other only on the next reload (looked like the choice «did not stick»).
try {
  globalThis.addEventListener?.('storage', (e: StorageEvent) => {
    if (e.key !== LOCALE_KEY && e.key !== null) return;
    const s = useI18n.getState();
    const n = normalizeLocale(safeStorage.get(LOCALE_KEY));
    if (n !== s.chosen) useI18n.setState({ chosen: n, locale: pickLocale(codes(s.locales), n, s.account, deviceLocale()) });
  });
} catch {
  /* no window (tests) */
}

/** Re-fetch translations every 10 min and when the tab becomes visible (edits in the CRM reach phones quickly). */
export function startI18nRefresh(): () => void {
  void useI18n.getState().load();
  const id = setInterval(() => void useI18n.getState().load(), I18N_REFRESH_MS);
  const onVis = () => {
    if (document.visibilityState === 'visible') void useI18n.getState().load();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => {
    clearInterval(id);
    document.removeEventListener('visibilitychange', onVis);
  };
}

/** Non-hook access (stores, toasts). */
export function t(key: DictKey, vars?: Vars): string {
  const s = useI18n.getState();
  return translate(s.strings, s.locale, key, vars);
}

/** Hook: re-renders when the language or the strings change. */
export function useT() {
  const locale = useI18n((s) => s.locale);
  const strings = useI18n((s) => s.strings);
  return useCallback((key: DictKey, vars?: Vars) => translate(strings, locale, key, vars), [strings, locale]);
}

export type T = ReturnType<typeof useT>;
export type { DictKey };
